import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Globe2, MapPin, UserRound, Clock3, ShieldCheck } from "lucide-react";
import { HERO, MAP_PINS, GUARANTEE } from "./content";
import { scrollToId } from "./utils";
import { PrimaryButton, SecondaryButton } from "./Primitives";
import WorldMap from "./WorldMap";

const fade = (delay) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export const HireHero = () => (
    <section
        id="top"
        data-testid="hire-hero"
        className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
    >
        <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-[520px] -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(45,91,255,0.08),transparent_70%)]"
        />
        <div className="container-page grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-6">
                <motion.div {...fade(0)}>
                    <span className="inline-flex items-center gap-2 rounded-full border border-hire-line bg-white px-3 py-1.5 text-xs font-medium text-hire-muted">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                        Specialist influencer marketing recruitment
                    </span>
                </motion.div>
                <motion.h1
                    {...fade(0.05)}
                    data-testid="hire-hero-headline"
                    className="mt-6 font-extrabold text-[40px] leading-[1.08] sm:text-5xl lg:text-[60px] text-hire-ink text-balance"
                >
                    {HERO.headline}
                </motion.h1>
                <motion.p
                    {...fade(0.12)}
                    className="mt-6 text-lg md:text-xl text-hire-ink/80 leading-relaxed text-pretty"
                >
                    {HERO.sub1}
                </motion.p>
                <motion.p
                    {...fade(0.16)}
                    className="mt-4 text-base md:text-[17px] text-hire-muted leading-relaxed text-pretty"
                >
                    {HERO.sub2}
                </motion.p>
                <motion.p
                    {...fade(0.2)}
                    className="mt-3 text-base md:text-[17px] font-medium text-hire-ink leading-relaxed"
                >
                    {HERO.sub3}
                </motion.p>
                <motion.div
                    {...fade(0.26)}
                    className="mt-9 flex flex-col sm:flex-row gap-3"
                >
                    <PrimaryButton
                        data-testid="hero-primary-cta"
                        onClick={() => scrollToId("hire-form")}
                        className="h-[52px] px-7"
                    >
                        {HERO.primaryCta}
                        <ArrowRight className="h-4 w-4" />
                    </PrimaryButton>
                    <SecondaryButton
                        data-testid="hero-secondary-cta"
                        onClick={() => scrollToId("how-it-works")}
                    >
                        {HERO.secondaryCta}
                    </SecondaryButton>
                </motion.div>
                <motion.div {...fade(0.3)} className="mt-8">
                    <span
                        data-testid="hero-guarantee-badge"
                        className="inline-flex items-center gap-2 rounded-full bg-hire-ink text-white pl-1.5 pr-4 py-1.5 text-sm font-medium"
                    >
                        <span className="h-6 w-6 rounded-full bg-brand-blue grid place-items-center">
                            <ShieldCheck className="h-3.5 w-3.5" />
                        </span>
                        <span className="font-semibold">{GUARANTEE.label}</span>
                        <span className="hidden sm:inline text-white/70">· {GUARANTEE.short}</span>
                    </span>
                </motion.div>
                <motion.ul
                    {...fade(0.34)}
                    className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-hire-muted"
                >
                    {["50+ cities", "Full-time or part-time", "Remote, in-person or hybrid"].map(
                        (t) => (
                            <li key={t} className="flex items-center gap-2">
                                <BadgeCheck className="h-4 w-4 text-brand-blue" />
                                {t}
                            </li>
                        )
                    )}
                </motion.ul>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="lg:col-span-6"
            >
                <TalentNetworkVisual />
            </motion.div>
        </div>
    </section>
);

const ProfileCard = ({ role, meta, tag, className, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        className={`rounded-xl border border-hire-line bg-white/95 backdrop-blur p-3 shadow-[0_14px_40px_-20px_rgba(11,15,25,0.25)] ${className}`}
    >
        <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-brand-blue-soft grid place-items-center text-brand-blue shrink-0">
                <UserRound className="h-4 w-4" />
            </div>
            <div className="min-w-0">
                <p className="text-[13px] font-semibold text-hire-ink leading-snug">{role}</p>
                <p className="text-[11.5px] text-hire-muted flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 shrink-0" /> {meta}
                </p>
            </div>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-[#ECFDF5] text-[#047857] px-1.5 py-0.5 text-[10.5px] font-semibold">
                <BadgeCheck className="h-3 w-3" /> Verified
            </span>
            <span className="rounded-md bg-hire-bg text-hire-muted px-1.5 py-0.5 text-[10.5px] font-medium">
                {tag}
            </span>
        </div>
    </motion.div>
);

const TalentNetworkVisual = () => (
    <div data-testid="hero-visual" className="relative">
        <div className="relative rounded-3xl border border-hire-line bg-white p-4 sm:p-6 shadow-[0_30px_80px_-40px_rgba(11,15,25,0.25)]">
            <div className="flex items-center justify-between text-xs text-hire-muted">
                <span className="inline-flex items-center gap-1.5 font-medium">
                    <Globe2 className="h-3.5 w-3.5 text-brand-blue" /> Global talent network
                </span>
                <span className="hidden sm:inline-flex items-center gap-1.5">
                    <Clock3 className="h-3.5 w-3.5" /> Shortlist in 5–7 days
                </span>
            </div>
            <WorldMap pins={MAP_PINS} className="mt-4 w-full h-auto" />
            <ProfileCard
                role="Influencer Marketing Specialist"
                meta="Remote · Senior (5+ yrs)"
                tag="Full-time"
                delay={0.6}
                className="hidden sm:block absolute left-6 top-[34%] w-[252px]"
            />
            <ProfileCard
                role="Head of Partnerships"
                meta="New York · In-person"
                tag="Long-term"
                delay={0.75}
                className="hidden sm:block absolute right-6 bottom-10 w-[236px]"
            />
            <div className="sm:hidden mt-4 grid gap-2">
                <ProfileCard role="Influencer Marketing Specialist" meta="Remote · Senior (5+ yrs)" tag="Full-time" delay={0.6} />
                <ProfileCard role="Head of Partnerships" meta="New York · In-person" tag="Long-term" delay={0.7} />
            </div>
            <p className="mt-3 text-[11px] text-hire-muted/80">
                Illustrative interface. Locations and roles shown as examples only.
            </p>
        </div>
    </div>
);

export default HireHero;
